import { useState, useRef } from 'react';
import { Search, Download, Save, Globe, ShoppingBag, AlertCircle, CheckCircle, Loader, Mail, Phone, ExternalLink } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const PLATFORMS = [
  { id: 'any',         label: 'All platforms',  icon: <Globe size={15} /> },
  { id: 'shopify',     label: 'Shopify',        icon: <ShoppingBag size={15} /> },
  { id: 'woocommerce', label: 'WooCommerce',    icon: <ShoppingBag size={15} /> },
  { id: 'etsy',        label: 'Etsy',           icon: <ShoppingBag size={15} /> },
  { id: 'squarespace', label: 'Squarespace',    icon: <Globe size={15} /> },
  { id: 'bigcommerce', label: 'BigCommerce',    icon: <ShoppingBag size={15} /> },
];

export default function WebScraper() {
  const [niche, setNiche] = useState('');
  const [location, setLocation] = useState('');
  const [platform, setPlatform] = useState('any');
  const [maxResults, setMaxResults] = useState(50);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('');
  const [leads, setLeads] = useState([]);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [listName, setListName] = useState('');
  const abortRef = useRef(null);

  const startScrape = async () => {
    if (!niche.trim()) return toast.error('Please enter a niche');
    setRunning(true);
    setLeads([]);
    setDone(false);
    setStatus('Starting search...');

    try {
      const token = localStorage.getItem('bulkify_token');
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const res = await fetch(`${baseUrl}/api/scraper/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ niche, location, platform, maxResults }),
      });

      const reader = res.body.getReader();
      abortRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'status') setStatus(data.message);
            if (data.type === 'lead') setLeads(prev => [...prev, data.lead]);
            if (data.type === 'done') {
              setStatus(data.message);
              setDone(true);
              setListName(`${niche} leads - ${new Date().toLocaleDateString()}`);
            }
            if (data.type === 'error') {
              toast.error(data.message);
              setStatus('Error: ' + data.message);
              setDone(true);
            }
          } catch {}
        }
      }
    } catch (err) {
      toast.error('Scraper failed: ' + err.message);
      setStatus('Failed');
    } finally {
      setRunning(false);
    }
  };

  const stop = () => {
    if (abortRef.current) {
      abortRef.current.cancel();
      setRunning(false);
      setDone(true);
      setStatus(`Stopped — ${leads.length} leads found`);
    }
  };

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Website', 'Platform', 'Niche', 'Location'];
    const rows = leads.map(l => [l.name, l.email, l.phone, l.website, l.platform, l.niche, l.location]
      .map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulkify-scraped-${niche.replace(/\s+/g,'-')}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded!');
  };

  const saveToLeads = async () => {
    if (leads.length === 0) return toast.error('No leads to save');
    setSaving(true);
    try {
      const res = await api.post('/api/scraper/save', { leads, listName });
      toast.success(`Saved ${res.data.imported} leads to "${res.data.listName}"!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save leads');
    } finally { setSaving(false); }
  };

  const leadsWithEmail = leads.filter(l => l.email);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Search size={20} color="var(--blue)" />
          <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Web Scraper</h1>
          <span className="badge badge-blue">Admin only</span>
        </div>
        <p style={{ color: 'var(--text2)', fontSize: 13 }}>Automatically find business leads from across the web</p>
      </div>

      {/* Search form */}
      <div className="card" style={{ padding: 22, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
              Niche / Business type <span style={{ color: 'var(--red)' }}>*</span>
            </label>
            <input className="input" placeholder="e.g. shoe stores, fashion boutiques, electronics" value={niche} onChange={e => setNiche(e.target.value)} disabled={running} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>Location (optional)</label>
            <input className="input" placeholder="e.g. Lagos, Nigeria, Abuja" value={location} onChange={e => setLocation(e.target.value)} disabled={running} />
          </div>
        </div>

        {/* Platform selector */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 8 }}>Platform</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PLATFORMS.map(p => (
              <button key={p.id} type="button"
                onClick={() => setPlatform(p.id)}
                disabled={running}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
                  border: `1px solid ${platform === p.id ? 'var(--blue)' : 'var(--border)'}`,
                  background: platform === p.id ? 'var(--blue-dim)' : 'var(--bg3)',
                  color: platform === p.id ? '#93c5fd' : 'var(--text2)',
                  cursor: running ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                }}>
                {p.icon} {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, color: 'var(--text2)' }}>Max results:</label>
            <select className="input" style={{ width: 80 }} value={maxResults} onChange={e => setMaxResults(parseInt(e.target.value))} disabled={running}>
              {[20, 50, 100, 150].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }} />
          {running ? (
            <button className="btn btn-danger" onClick={stop}>
              Stop scraping
            </button>
          ) : (
            <button className="btn btn-primary" onClick={startScrape} disabled={!niche.trim()}>
              <Search size={14} /> Start scraping
            </button>
          )}
        </div>
      </div>

      {/* Status bar */}
      {(running || done || status) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 16px', borderRadius: 'var(--radius)',
          background: running ? 'rgba(59,130,246,0.08)' : done && leads.length > 0 ? 'rgba(34,197,94,0.08)' : 'var(--bg2)',
          border: `1px solid ${running ? 'rgba(59,130,246,0.2)' : done && leads.length > 0 ? 'rgba(34,197,94,0.2)' : 'var(--border)'}`,
          marginBottom: 16,
        }}>
          {running
            ? <Loader size={15} color="var(--blue)" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            : done && leads.length > 0
            ? <CheckCircle size={15} color="var(--green)" style={{ flexShrink: 0 }} />
            : <AlertCircle size={15} color="var(--text3)" style={{ flexShrink: 0 }} />
          }
          <span style={{ fontSize: 13, color: 'var(--text2)', flex: 1 }}>{status}</span>
          {done && leads.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                {leadsWithEmail.length} with email / {leads.length} total
              </span>
              <span className="badge badge-green">{leads.length} found</span>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {leads.length > 0 && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Results</span>
              <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 10 }}>
                {leads.length} leads · {leadsWithEmail.length} with email
              </span>
            </div>
            {done && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="input" placeholder="List name…" value={listName} onChange={e => setListName(e.target.value)} style={{ width: 200 }} />
                <button className="btn btn-ghost btn-sm" onClick={saveToLeads} disabled={saving}>
                  <Save size={13} /> {saving ? 'Saving…' : 'Save to leads'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={downloadCSV}>
                  <Download size={13} /> Download CSV
                </button>
              </div>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['#', 'Business name', 'Email', 'Phone', 'Website'].map(h => (
                  <th key={h} style={{ padding: '9px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)', opacity: running && i === leads.length - 1 ? 0.6 : 1 }}>
                  <td style={{ padding: '10px 16px', fontSize: 12, color: 'var(--text3)' }}>{i + 1}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</div>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {lead.email ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                        <Mail size={11} color="var(--green)" />
                        <span style={{ color: 'var(--green)' }}>{lead.email}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    {lead.phone ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                        <Phone size={11} color="var(--text3)" />
                        <span style={{ color: 'var(--text2)' }}>{lead.phone}</span>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text3)' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <a href={lead.website} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: 'var(--blue)', display: 'flex', alignItems: 'center', gap: 4, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <ExternalLink size={11} />
                      {lead.website.replace(/^https?:\/\//, '').slice(0, 30)}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
